import { useForm, zodResolver } from '@mantine/form'
import { format, parseISO } from 'date-fns'
import { enUS, es, pt } from 'date-fns/locale'
import { useCallback, useMemo } from 'react'
import { z } from 'zod'

import { useTranslation } from '@flecto/translations'
import { type CoreBookingDocument } from '@flecto/types'

import { useBookingStore } from './useBookingStore'

type UseEmailFormProps = {
  template?: { value: TemplateType }
  customer_language?: string
  document?: CoreBookingDocument
  bookLink?: string
}

export const EMAIL_TEMPLATES = {
  others: {
    subject: 'send_email.templates.custom.subject',
    message: 'send_email.templates.custom.message',
  },
  custom: {
    subject: 'send_email.templates.custom.subject',
    message: 'send_email.templates.custom.message',
  },
  quote: {
    subject: 'send_email.templates.quote.subject',
    message: 'send_email.templates.quote.message',
  },
  contract: {
    subject: 'send_email.templates.contract.subject',
    message: 'send_email.templates.contract.message',
  },
  invoice: {
    subject: 'send_email.templates.invoice.subject',
    message: 'send_email.templates.invoice.message',
  },
  proforma: {
    subject: 'send_email.templates.proforma.subject',
    message: 'send_email.templates.proforma.message',
  },
} as const

export type TemplateType = keyof typeof EMAIL_TEMPLATES

export const useSendEmailForm = ({
  template,
  customer_language = 'en',
  document,
  bookLink,
}: UseEmailFormProps = {}) => {

  const { t } = useTranslation('saas', {
    keyPrefix: 'booking',
  })
  const { t: t_customer } = useTranslation('saas', {
    keyPrefix: 'booking',
    lng: customer_language,
  })

  const localeMap = {
    en: enUS,
    es: es,
    pt: pt,
  }
  const selectedLocale =
    localeMap[customer_language as keyof typeof localeMap] || enUS

  const { booking, documents: allDocuments = [] } = useBookingStore()

  const findMostRecentMatchingDocument = ():
    | CoreBookingDocument
    | undefined => {
    if (!template?.value || !allDocuments?.length) {
      return undefined
    }

    const matchingDocs = allDocuments
      ?.filter((doc) => doc.category === template.value)
      ?.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )

    return matchingDocs[0]
  }

  const mostRecentMatchingDocument =
    document ?? findMostRecentMatchingDocument()
  const hasMatchingDocument = Boolean(document || mostRecentMatchingDocument)

  const formatDocumentId = (document?: CoreBookingDocument): string =>
    document?.filename_base ?? ''

  const schema = z.object({
    subject: z
      .string()
      .trim()
      .min(1, { message: t('send_email.field_required') }),
    message: z
      .string()
      .trim()
      .transform((value) => value.replace(/<p[^>]*>\s*<\/p>/g, ''))
      .refine((value) => value.length > 0, {
        message: t('send_email.field_required'),
      }),

    documents: z.array(z.number()).optional(),
  })

  const checkDocumentConditions = useCallback(
    (documentId: number, checkUnsigned: boolean = false): boolean => {
      if (!documentId) return false

      const document = allDocuments?.find((doc) => doc.id === documentId)

      if (!document) return false

      const isGenerated = document.generated
      const includeSignatureField =
        document?.snapshot?.booking_settings
          ?.documents_quote_include_signatures ||
        document?.snapshot?.booking_settings
          ?.documents_contract_include_signatures

      const meetsBaseConditions = isGenerated && includeSignatureField

      if (checkUnsigned) {
        const isUnsigned = !document.signed_at
        return Boolean(meetsBaseConditions && isUnsigned)
      }

      return Boolean(meetsBaseConditions)
    },
    [allDocuments]
  )

  const checkDocumentBaseConditions = useCallback(
    (documentId: number): boolean => {
      return checkDocumentConditions(documentId)
    },
    [checkDocumentConditions]
  )

  const hasUnsignedDocuments = useCallback(
    (documentIds: number[]): number[] => {
      if (!documentIds || documentIds.length === 0) return []

      return documentIds.filter((documentId) =>
        checkDocumentConditions(documentId, true)
      )
    },
    [checkDocumentConditions]
  )

  const generateMessage = useCallback((): string => {
    const datesMissing = !booking?.start_at || !booking?.end_at

    const baseMessageKey = String(
      EMAIL_TEMPLATES[template?.value ?? 'custom']?.message
    )

    const finalMessageKey =
      template?.value === 'custom' || template?.value === 'others'
        ? baseMessageKey
        : `${baseMessageKey}${
            datesMissing ? '_without_dates' : '_with_dates'
          }${hasMatchingDocument ? '_with_doc' : '_without_doc'}`

    const translationOptions: {
      id?: string
      from?: string
      to?: string
      booking_link?: string
    } = {}

    if (
      hasMatchingDocument &&
      template?.value !== 'custom' &&
      template?.value !== 'others'
    ) {
      translationOptions.id = formatDocumentId(
        document || mostRecentMatchingDocument
      )
    }

    translationOptions.booking_link = bookLink ?? ''

    if (!datesMissing && booking?.start_at && booking?.end_at) {
      try {
        const startDate = parseISO(booking.start_at)
        const endDate = parseISO(booking.end_at)
        translationOptions.from = format(
          startDate,
          t_customer('send_email.date_time_format'),
          { locale: selectedLocale }
        )
        translationOptions.to = format(
          endDate,
          t_customer('send_email.date_time_format'),
          { locale: selectedLocale }
        )
      } catch (error) {
        console.error('Error formatting booking dates:', error)
        translationOptions.from = booking.start_at
        translationOptions.to = booking.end_at
      }
    }

    return t_customer(finalMessageKey, translationOptions)
  }, [
    booking?.start_at,
    booking?.end_at,
    document,
    EMAIL_TEMPLATES,
    formatDocumentId,
    hasMatchingDocument,
    localeMap,
    mostRecentMatchingDocument,
    selectedLocale,
    t_customer,
    template?.value,
    bookLink,
  ])

  const initialValues = useMemo(
    () => ({
      subject: template
        ? (() => {
            const baseSubjectKey = String(
              EMAIL_TEMPLATES[template.value]?.subject
            )

            const finalSubjectKey =
              template.value === 'custom' || template.value === 'others'
                ? baseSubjectKey
                : `${baseSubjectKey}${
                    hasMatchingDocument ? '_with_doc' : '_without_doc'
                  }`

            const translationOptions: { id?: string } = {}
            if (
              hasMatchingDocument &&
              template.value !== 'custom' &&
              template.value !== 'others'
            ) {
              translationOptions.id = formatDocumentId(
                document || mostRecentMatchingDocument
              )
            }

            return t_customer(finalSubjectKey, translationOptions)
          })()
        : '',
      message: template ? generateMessage() : '',
      documents: (() => {
        if (document?.id) {
          return [document.id as number]
        }
        if (mostRecentMatchingDocument?.id) {
          return [mostRecentMatchingDocument.id as number]
        }
        return []
      })(),
    }),
    [
      document,
      EMAIL_TEMPLATES,
      formatDocumentId,
      generateMessage,
      hasMatchingDocument,
      mostRecentMatchingDocument,
      t_customer,
      template,
    ]
  )

  const formZod = useForm({
    validate: zodResolver(schema),
    initialValues,
  })

  return {
    ...formZod,
    initialValues,
    hasUnsignedDocuments,
    checkDocumentBaseConditions,
    validateFields: () => formZod.validate(),
  }
}
